import * as React from "react";
import styled from "styled-components";

import Pill from "@ingka/pill";

import Button from "@ingka/button";

import { Application } from "../../store";
import Status from "@ingka/status";
import Tabs, { Tab, TabPanel } from "@ingka/tabs";
import people from "@ingka/ssr-icon/paths/people";
import magnifyingGlass from "@ingka/ssr-icon/paths/magnifying-glass";
import Avatar from "@ingka/avatar";
import Chart from "../Chart";
import Accordion, { AccordionItem } from "@ingka/accordion";
import documentPencil from "@ingka/ssr-icon/paths/document-pencil";
import text from "@ingka/ssr-icon/paths/text";
import list from "@ingka/ssr-icon/paths/list";
import database from "@ingka/ssr-icon/paths/database";
import Table, { TableBody } from "@ingka/table";
import Skeleton from "@ingka/skeleton";
import InlineMessage from "@ingka/inline-message";
import Search from "@ingka/search";
import { OverflowCarousel } from "@ingka/carousel";
import { breakpointL, breakpointM, thicknessThin } from "@ingka/variables/design-tokens";
import SSRIcon from "@ingka/ssr-icon";
import { colourNeutral3 } from "@ingka/variables/colours-css";

import MarketUsersModal from "./MarketUsersModal/MarketUsersModal";
import { capitalizeFirstLetter } from "./utils";
import useWindowSize from "../../hooks/useWindowSize";
import { SrOnly } from "../styles";
import LoadingAnnouncer from "../LoadingAnnouncer";
import useLoadingAnnouncer from "../LoadingAnnouncer/useLoadingAnnouncer";

interface FeaturesTableProps {
	loading: boolean;
	activeApplication?: Application;
	hasGroups?: boolean;
	marketData: any;
	tabsData: any;
	setActiveTab: (data: string) => void;
	tableData?: any[];
	activeMarket: string | null;
	activeTab: string;
	showMarketUsersModal: boolean;
	setShowMarketUsersModal: (value: boolean) => void;
	showSeachBar: boolean;
	setShowSeachBar: (value: boolean) => void;
	searchPropertyValue: string;
	setSearchPropertyValue: (value: string) => void;
	marketDashboardChartsData: any;
	showAdoptionRate: boolean | undefined;
}

const InfoList = styled.dl`
	@media only screen and (max-width: ${breakpointL}) {
		width: 80vw;
	}
	width: 60vw;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const InfoRow = styled.div`
	padding: 0.75rem 0.5rem 0.75rem 0rem !important;
	border-bottom: ${thicknessThin} solid ${colourNeutral3};
	justify-content: space-between;
	display: flex;
	gap: 8px;
`;

const SecondColumnCellTable = styled.td`
	display: flex;
	width: auto;
	justify-content: end;
`;

const MarketTableInfoList = styled.div`
	display: flex;
	flex-direction: column;

	@media only screen and (max-width: ${breakpointM}) {
		width: calc(100vw - 2.5rem);
	}
`;

const TableHeader = styled.div`
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-top: 1rem;
`;

const TableHeaderButtonsInfoList = styled.div`
	display: flex;
	gap: 1rem;
`;

const StyledAccordionItem = styled(AccordionItem)`
	position: relative;
	box-shadow: 0px 0px 10px 0px lightgrey;
	margin-bottom: 1.5rem;
	padding-left: 1rem;
	padding-right: 1rem;

	.accordion-item-header {
		border: none;
	}
	.accordion-item-header__title {
		display: flex;
		align-items: center;
		justify-content: space-between;

		.custom-header {
			display: flex;
			align-items: center;
			gap: 1rem;
		}
	}
`;

const StyledListInfoList = styled.div`
	display: flex;
	width: auto;
	justify-content: end;
	text-align: left;
`;

const StyledInnerTable = styled.table`
	width: 30vw !important;

	th {
		padding: 0.75rem !important;
		border: 0.5px solid lightgrey;
		text-align: center;
		font-weight: bold;
	}

	td {
		padding: 1rem !important;
		border: 0.5px solid lightgrey;
		text-align: center;
	}

	td:has(:not(table)) {
		padding: 0rem !important;
	}
`;

const StyledInnerColumnTable = styled.table`
	width: 15vw !important;
	table-layout: auto;

	th {
		text-align: center;
		font-weight: bold;
		border-top: 0rem;
		border-bottom: 0rem;
		width: 2rem !important;
		overflow: hidden;
	}

	th:first-child {
		border-right: 0;
		border-left: 0;
	}

	th:last-child {
		border-right: 0;
	}

	td {
		text-align: center;
		width: 2rem !important;
		padding: 0.75rem !important;
		border-bottom: 0rem;
		overflow: hidden;
	}

	tr td:first-child {
		border-left: 0rem;
		border-right: 0rem;
	}

	tr td:last-child {
		border-right: 0;
	}
`;

const CircularSkeleton = styled(Skeleton)`
	border-radius: 100%;
	margin-bottom: 1rem;
`;

const ChartSkeletonLoadWrapper = styled.div`
	display: flex;
`;

const ChartSkeletonInfoList = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	margin-right: 3rem;
`;

const PaddingTop = styled.div`
	padding-top: 2rem;
`;

const ChartSkeleton = () => {
	return (
		<ChartSkeletonLoadWrapper>
			{[1, 2, 3, 4, 5].map(() => (
				<ChartSkeletonInfoList>
					<CircularSkeleton width="6rem" height="6rem" />
					<Skeleton width="6rem" height="1.4rem" />
				</ChartSkeletonInfoList>
			))}
		</ChartSkeletonLoadWrapper>
	);
};

const FeaturesTableSkeleton = () => {
	return (
		<div aria-label="Loading data...">
			{[1, 2, 3, 4].map(() => (
				<>
					<PaddingTop />
					<Skeleton width="70vw" height="3.5rem" />
				</>
			))}
		</div>
	);
};

const TabPanelData: React.FC<{ tableData?: any[] }> = ({ tableData }) => {
	const { isMobile } = useWindowSize();

	if (!tableData) {
		return null;
	}
	return (
		<>
			<Accordion collapsible={false} size="small">
				{tableData.map((tableItem, index) => (
					<StyledAccordionItem
						key={index}
						id={index.toString()}
						caption={tableItem.description || ""}
						title={
							<>
								<span>
									{tableItem["flagga-label"]
										? capitalizeFirstLetter(tableItem["flagga-label"])
										: capitalizeFirstLetter(tableItem.key)}
								</span>
								{!isMobile && (
									<span className="custom-header">
										{typeof tableItem.value === "boolean" && <Status statusColor={tableItem.value ? "green" : "red"} />}
										{typeof tableItem.value === "string" && (
											<SSRIcon
												type="tertiary"
												paths={text}
												ariaHidden={false}
												aria-label="Updated property is of type string"
											/>
										)}
										{Array.isArray(tableItem.value) &&
											tableItem.value.length > 0 &&
											typeof tableItem.value[0] === "string" && (
												<SSRIcon
													type="tertiary"
													paths={list}
													ariaHidden={false}
													aria-label="Updated property is of type array of string"
												/>
											)}
										{Array.isArray(tableItem.value) &&
											tableItem.value.length > 0 &&
											typeof tableItem.value[0] === "object" && (
												<SSRIcon
													type="tertiary"
													paths={database}
													aria-label="Updated property is of type array of objects"
												/>
											)}
										{tableItem.reason && (
											<SSRIcon
												type="tertiary"
												paths={documentPencil}
												aria-label="Reason was provided for this update"
											/>
										)}
										{tableItem.updatedBy?.name && (
											<Avatar
												screenReaderText={tableItem.updatedBy.name}
												text={tableItem.updatedBy.name.split("(")[0].replace(/[^A-Z]/g, "")}
												size="small"
											/>
										)}
									</span>
								)}
							</>
						}
					>
						<InfoList>
							<InfoRow>
								<dt>Last update</dt>
								<dd>{tableItem.timestamp ? new Date(tableItem.timestamp).toLocaleDateString() : "-"}</dd>
							</InfoRow>
							<InfoRow>
								<dt>Comment</dt>
								<dd>{tableItem.reason || "-"}</dd>
							</InfoRow>
							<InfoRow>
								<dt>Email</dt>
								<dd>{tableItem.updatedBy?.email || "-"}</dd>
							</InfoRow>
							<InfoRow>
								<dt>Value</dt>
								<dd>
									{typeof tableItem.value === "boolean" ? (
										<Status
											statusColor={tableItem.value ? "green" : "red"}
											label={tableItem.value ? "Active" : "Inactive"}
										/>
									) : typeof tableItem.value === "string" ? (
										<span>{tableItem.value}</span>
									) : Array.isArray(tableItem.value) && typeof tableItem.value[0] === "string" ? (
										<StyledListInfoList>
											<ul>
												{tableItem.value.map((item: any, i: number) => (
													<li key={i}>{item}</li>
												))}
											</ul>
										</StyledListInfoList>
									) : Array.isArray(tableItem.value) && typeof tableItem.value[0] === "object" ? (
										<SecondColumnCellTable>
											<StyledInnerTable className="table">
												<thead>
													<tr>
														{Object.keys(tableItem.value[0]).map((key) => (
															<th key={key}>{capitalizeFirstLetter(key)}</th>
														))}
													</tr>
												</thead>
												<TableBody>
													{tableItem.value.map((item: any, i: number) => (
														<tr key={i}>
															{Object.keys(item).map((itemKey) => (
																<td key={itemKey}>
																	{Array.isArray(item[itemKey]) && typeof item[itemKey][0] === "object" ? (
																		<StyledInnerColumnTable>
																			<thead>
																				<tr>
																					{Object.keys(item[itemKey][0]).map((innerKey) => (
																						<th key={innerKey}>{capitalizeFirstLetter(innerKey)}</th>
																					))}
																				</tr>
																			</thead>
																			<TableBody>
																				{item[itemKey].map((innerItem: any, j: number) => (
																					<tr key={j}>
																						{Object.keys(innerItem).map((innerKey) => (
																							<td key={innerKey}>{innerItem[innerKey]}</td>
																						))}
																					</tr>
																				))}
																			</TableBody>
																		</StyledInnerColumnTable>
																	) : (
																		item[itemKey]
																	)}
																</td>
															))}
														</tr>
													))}
												</TableBody>
											</StyledInnerTable>
										</SecondColumnCellTable>
									) : (
										"-"
									)}
								</dd>
							</InfoRow>
						</InfoList>
					</StyledAccordionItem>
				))}
			</Accordion>
			<div aria-live="polite">
				{tableData.length === 0 ? (
					<InlineMessage title="No Data to display" variant="informative" />
				) : (
					<SrOnly>{`${tableData.length} results found`}</SrOnly>
				)}
			</div>
		</>
	);
};

const Layout: React.FC<FeaturesTableProps> = ({
	activeApplication,
	hasGroups,
	tabsData,
	setActiveTab,
	tableData,
	activeMarket,
	activeTab,
	loading,
	showMarketUsersModal,
	setShowMarketUsersModal,
	setSearchPropertyValue,
	marketDashboardChartsData,
	showAdoptionRate,
}) => {
	const tabs = React.useMemo(() => {
		return tabsData?.groupIds?.map((tab: string) => (
			<Tab
				id={`tab-${tab}`}
				key={`tab-${tab}`}
				text={tab}
				type="button"
				tabPanelId={`tabpanel-${tab.toLowerCase()}`}
				onClick={() => {
					setActiveTab(tab);
				}}
			/>
		));
	}, [tabsData, setActiveTab]);

	const tabPanels = React.useMemo(() => {
		return tabsData?.groupIds?.map((tab: string) => (
			<TabPanel hasFocusableContent tabPanelId={`tabpanel-${tab.toLowerCase()}`} key={`tabpanel-${tab}`}>
				{loading ? <FeaturesTableSkeleton /> : <TabPanelData tableData={tableData} />}
			</TabPanel>
		));
	}, [loading, tableData, tabsData?.groupIds]);

	useLoadingAnnouncer("marketDashboard", loading);

	return (
		<>
			<LoadingAnnouncer loadingMessage="Loading dashboard" completedMessage="Loading completed" />
			<MarketTableInfoList>
				{showAdoptionRate && (
					<>
						<h2>Adoption rate per market</h2>
						<OverflowCarousel controlBtnType="emphasised" id="chart-InfoList">
							{loading ? (
								<ChartSkeleton />
							) : (
								marketDashboardChartsData?.map((chart: any) => (
									<Chart colorBar={chart.colorBar} label={chart.label} series={chart.series} />
								))
							)}
						</OverflowCarousel>
					</>
				)}
				<TableHeader>
					<h2>Overview</h2>
					<TableHeaderButtonsInfoList>
						<Button
							iconOnly
							text={`${showMarketUsersModal ? "Hide" : "Show"} assigned user information`}
							ssrIcon={people}
							onClick={() => setShowMarketUsersModal(true)}
							aria-haspopup="dialog"
						/>
					</TableHeaderButtonsInfoList>
					<Search
						ariaLabel="Search for product categories, product names, etc."
						clearBtnText="Clear search field"
						id="search"
						onSearch={(_, { value }) => setSearchPropertyValue(value)}
						onChange={(_, { value }) => setSearchPropertyValue(value)}
						placeholder="Search"
						submitBtnText="Submit"
						onClear={() => setSearchPropertyValue("")}
					/>
				</TableHeader>
				{hasGroups && tabs && (
					<Tabs tabs={tabs} activeTab={`tabpanel-${activeTab.toLowerCase()}`} tabPanels={tabPanels} />
				)}
			</MarketTableInfoList>
			<MarketUsersModal
				activeApplication={activeApplication}
				activeMarket={activeMarket}
				showMarketUsersModal={showMarketUsersModal}
				setShowMarketUsersModal={setShowMarketUsersModal}
			/>
		</>
	);
};

export default Layout;
